import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';

const LoanApprovalsModule = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [approveDialog, setApproveDialog] = useState({
    open: false,
    record: null,
    mode: 'FORMAL',
    note: ''
  });
  const [busy, setBusy] = useState(false);
  const canApproveRole = [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const canApproveRecord = (record) => {
    if (!canApproveRole) return false;
    if (user?.role === ROLES.AM_MRO && record?.storeApprovedBy) {
      const prev = String(record.storeApprovedBy._id || record.storeApprovedBy);
      const me = String(user.id || user._id || '');
      if (me && prev === me) return false;
    }
    return true;
  };
  const canApprove = canApproveRole;
  const canStoreApprove = user?.role === ROLES.STORE_INSPECTOR;
  const queueStatus = canStoreApprove ? 'PENDING_STORE_APPROVAL' : 'PENDING_AM_APPROVAL';

  const loadRows = useCallback(async () => {
    try {
      const res = await axios.get('/api/store/loan/issuances', {
        params: {
          loanDirection: 'LOAN_OUT',
          approvalStatus: queueStatus
        }
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      showToast('Failed to load loan approvals', 'error');
    }
  }, [showToast, queueStatus]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const uploadApprovalAttachment = async (recordId, file) => {
    const form = new FormData();
    form.append('file', file);
    await axios.post(`/api/store/loan/issue/${recordId}/approval-attachment`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  };

  const approve = async () => {
    if (!approveDialog.record) return;
    if (approveDialog.mode === 'VERBAL' && !approveDialog.note.trim()) {
      showToast('Verbal approval note is required', 'error');
      return;
    }
    try {
      setBusy(true);
      await axios.post(`/api/store/loan/issue/${approveDialog.record._id}/approve`, {
        approvalMode: approveDialog.mode,
        approvalNote: approveDialog.note
      });
      showToast('Loan out approved and issued', 'success');
      setApproveDialog({ open: false, record: null, mode: 'FORMAL', note: '' });
      loadRows();
    } catch (error) {
      showToast(error.response?.data?.message || 'Approval failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (recordId) => {
    try {
      setBusy(true);
      const reason = canStoreApprove
        ? 'Rejected by Store Manager'
        : 'Rejected by AM';
      await axios.post(`/api/store/loan/issue/${recordId}/reject`, { reason });
      showToast('Loan out rejected — Store Executive can revise and resubmit', 'success');
      loadRows();
    } catch (error) {
      showToast(error.response?.data?.message || 'Reject failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const forwardToAm = async (recordId) => {
    try {
      setBusy(true);
      await axios.post(`/api/store/loan/issue/${recordId}/submit-approval`);
      showToast('Loan out approved by Store Manager and sent to AM', 'success');
      loadRows();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to forward for AM approval', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Loan Approvals (AM)</Typography>
      {!canApprove && !canStoreApprove && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only Store Manager or Accountable Manager can process loan-out approvals.
        </Alert>
      )}
      {canStoreApprove && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Store Manager queue: review Store Executive loan-out requests before AM approval.
        </Alert>
      )}
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Approval</TableCell>
              {(canApprove || canStoreApprove) && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</TableCell>
                <TableCell>{row.counterpartyCompany || '—'}</TableCell>
                <TableCell>{row.loanReferenceNumber || '—'}</TableCell>
                <TableCell>{Array.isArray(row.items) ? row.items.length : 0}</TableCell>
                <TableCell><Chip size="small" label={row.approvalStatus || '—'} /></TableCell>
                {(canApprove || canStoreApprove) && (
                  <TableCell>
                    {canStoreApprove ? (
                      <>
                        <Button size="small" variant="outlined" onClick={() => forwardToAm(row._id)} disabled={busy}>
                          Approve & Send to AM
                        </Button>
                        <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => reject(row._id)} disabled={busy}>
                          Reject
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setApproveDialog({ open: true, record: row, mode: 'FORMAL', note: '' })}
                          disabled={busy || !canApproveRecord(row)}
                          title={
                            !canApproveRecord(row)
                              ? 'AM MRO cannot perform two consecutive approvals — another user must complete this AM step.'
                              : undefined
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          sx={{ ml: 1 }}
                          onClick={() => reject(row._id)}
                          disabled={busy || !canApproveRecord(row)}
                          title={
                            !canApproveRecord(row)
                              ? 'AM MRO cannot perform two consecutive approvals — another user must complete this AM step.'
                              : undefined
                          }
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          component="label"
                          disabled={busy}
                        >
                          Upload Verbal Proof
                          <input
                            hidden
                            type="file"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              e.target.value = '';
                              if (!file) return;
                              try {
                                setBusy(true);
                                await uploadApprovalAttachment(row._id, file);
                                showToast('Attachment uploaded', 'success');
                                loadRows();
                              } catch (error) {
                                showToast(error.response?.data?.message || 'Upload failed', 'error');
                              } finally {
                                setBusy(false);
                              }
                            }}
                          />
                        </Button>
                      </>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={approveDialog.open && canApprove} onClose={() => setApproveDialog({ open: false, record: null, mode: 'FORMAL', note: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Loan-Out</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Approval Mode"
            value={approveDialog.mode}
            sx={{ mt: 1 }}
            onChange={(e) => setApproveDialog((prev) => ({ ...prev, mode: e.target.value }))}
          >
            <option value="FORMAL">FORMAL</option>
            <option value="VERBAL">VERBAL</option>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={approveDialog.mode === 'VERBAL' ? 'Verbal Approval Note (required)' : 'Approval Note'}
            sx={{ mt: 2 }}
            value={approveDialog.note}
            onChange={(e) => setApproveDialog((prev) => ({ ...prev, note: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, record: null, mode: 'FORMAL', note: '' })}>Cancel</Button>
          <Button variant="contained" onClick={approve} disabled={busy}>Approve & Issue</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanApprovalsModule;
